import { lazy } from 'react';

import { Redirect } from 'react-router-dom';

import { DNS_ID, PRIVACY_ID, TOS_ID } from '@m/constants/AgreementTypes';

import AccountDeletePage from '../containers/AccountDeletePage';

import { LazyRoute } from './LazyRoute';
import {
  PublicRoutes,
  ProtectedRoutes,
  publicPaths,
  defaultOnboardingPaths,
  startParkingOnboardingPaths,
  taskItemPaths,
  referralOnboardingPaths,
} from './paths';

const TermsContainer = lazy(() => import('apps/customer/containers/TermsContainer'));
const HelpRouter = lazy(() => import('apps/customer/components/HelpRouter'));
const ClaimPage = lazy(() => import('apps/customer/containers/Claim'));
const ReserveParkingCheckoutPage = lazy(
  () => import('apps/customer/containers/ReserveParkingCheckout/ReserveParkingCheckout'),
);
const ReserveParkingConfirmationPage = lazy(
  () => import('apps/customer/containers/ReserveParkingConfirmation/ReserveParkingConfrimation'),
);
const TestingPage = lazy(() => import('apps/customer/containers/TestingPage/TestingPage'));

// /task handler
const PaymentOverduePage = lazy(() => import('apps/customer/containers/PaymentOverduePage'));

// onboarding
const WelcomePage = lazy(() => import('apps/customer/containers/WelcomePage'));
const ReferralOnboardingPage = lazy(() => import('apps/customer/containers/ReferralOnboarding'));
const StartParkingPage = lazy(() => import('apps/customer/containers/StartParkingPage'));
const ConfirmSubscription = lazy(
  () => import('apps/customer/containers/ConfirmSubscription/ConfirmSubscription'),
);
const SubscriptionConfirmed = lazy(
  () => import('apps/customer/containers/SubscriptionConfirmed/SubscriptionConfirmed'),
);

// signed in
const MembershipContainer = lazy(() => import('apps/customer/containers/MembershipContainer'));
const ParkNowContainer = lazy(
  () => import('apps/customer/containers/ParkNowContainer/ParkNowContainer'),
);

const VisitPage = lazy(() => import('apps/customer/containers/VisitPage'));
const PaymentPage = lazy(() => import('apps/customer/containers/PaymentPage'));
const ProfileContainer = lazy(() => import('apps/customer/containers/ProfileContainer'));
const RedeemContainer = lazy(() => import('apps/customer/containers/RedeemContainer'));
const VehicleContainer = lazy(() => import('apps/customer/containers/VehicleContainer'));
const VisitReceiptPage = lazy(() => import('apps/customer/components/VisitReceiptPage'));
const ConfirmMissedEntryPage = lazy(
  () => import('apps/customer/containers/ConfirmMissedEntryPage'),
);
const DashboardPage = lazy(() => import('apps/customer/containers/Dashboard'));
const ActivityPage = lazy(() => import('apps/customer/containers/Activity/Activity'));
const PromotionsPage = lazy(() => import('apps/customer/containers/Promotions'));
const RewardsPage = lazy(() => import('apps/customer/containers/Rewards'));
const LinkAccountPage = lazy(() => import('apps/customer/containers/Rewards/LinkAccount'));
const PassReceiptPage = lazy(() => import('apps/customer/containers/ReceiptPage'));

export const onboardingRoutesList = ['/welcome']
  .concat(defaultOnboardingPaths)
  .concat(publicPaths)
  .concat(referralOnboardingPaths);

export const publicRoutesList = [
  {
    path: PublicRoutes.Terms,
    component: LazyRoute,
    children: <TermsContainer agreementType={TOS_ID} />,
  },
  {
    path: PublicRoutes.Privacy,
    component: LazyRoute,
    children: <TermsContainer agreementType={PRIVACY_ID} />,
  },
  {
    path: PublicRoutes.DoNotSell,
    component: LazyRoute,
    children: <TermsContainer agreementType={DNS_ID} />,
  },
  {
    exact: false,
    path: PublicRoutes.Help,
    component: LazyRoute,
    children: <HelpRouter />,
  },
  {
    exact: false,
    path: PublicRoutes.Claim,
    component: LazyRoute,
    children: <ClaimPage />,
  },
  {
    exact: true,
    path: PublicRoutes.ReserveParking,
    component: LazyRoute,
    children: <ReserveParkingCheckoutPage />,
  },
  {
    exact: true,
    path: PublicRoutes.Testing,
    component: LazyRoute,
    children: <TestingPage />,
  },
];

export const taskRoutesList = () => [
  {
    exact: true,
    path: taskItemPaths,
    component: LazyRoute,
    children: <PaymentOverduePage />,
  },
];

function welcomeRoute({
  isInviteWelcome,
  isReferredMember,
}: {
  isInviteWelcome?: boolean | null;
  isReferredMember?: boolean | null;
}) {
  if (isReferredMember) {
    return {
      path: '/welcome',
      component: LazyRoute,
      children: <ReferralOnboardingPage />,
    };
  }
  if (isInviteWelcome) {
    return {
      path: '/welcome',
      component: LazyRoute,
      children: <WelcomePage />,
    };
  }
  return null;
}

export const customerRoutesList = ({
  isInviteWelcome,
  isReferredMember,
  isMostRecentVisitOpen,
}: {
  isInviteWelcome?: boolean | null;
  isMostRecentVisitOpen: boolean | null;
  isReferredMember?: boolean | null;
}) => [
  // onboarding
  welcomeRoute({ isInviteWelcome, isReferredMember }),
  {
    exact: true,
    path: startParkingOnboardingPaths,
    component: LazyRoute,
    children: <StartParkingPage />,
  },
  // signed in
  {
    exact: true,
    path: ProtectedRoutes.ForwardSlash,
    component: LazyRoute,
    children:
      isMostRecentVisitOpen === null ? null : (
        <Redirect
          to={isMostRecentVisitOpen ? ProtectedRoutes.VisitPage : ProtectedRoutes.DashboardPage}
        />
      ),
  },
  {
    path: ProtectedRoutes.VisitPage,
    component: LazyRoute,
    exact: true,
    children: <VisitPage />,
  },
  {
    path: ProtectedRoutes.PaymentPage,
    component: LazyRoute,
    children: <PaymentPage />,
  },
  {
    exact: true,
    path: ProtectedRoutes.ProfilePage,
    component: LazyRoute,
    children: <ProfileContainer />,
  },
  {
    exact: true,
    path: ProtectedRoutes.AccountDeletePage,
    component: LazyRoute,
    children: <AccountDeletePage />,
  },
  {
    path: ProtectedRoutes.MembershipPage,
    component: LazyRoute,
    children: <MembershipContainer />,
  },
  {
    path: ProtectedRoutes.ParkNowPage,
    component: LazyRoute,
    children: <ParkNowContainer />,
  },
  {
    path: ProtectedRoutes.RedeemPage,
    component: LazyRoute,
    children: <RedeemContainer />,
  },
  {
    path: ProtectedRoutes.VehiclePage,
    component: LazyRoute,
    children: <VehicleContainer />,
  },
  {
    path: ProtectedRoutes.VisitReceiptPage,
    component: LazyRoute,
    exact: true,
    children: <VisitReceiptPage />,
  },
  {
    path: ProtectedRoutes.DashboardPage,
    component: LazyRoute,
    exact: true,
    children: isMostRecentVisitOpen === null ? null : <DashboardPage />,
  },
  {
    path: ProtectedRoutes.ActivityPage,
    component: LazyRoute,
    exact: true,
    children: <ActivityPage />,
  },
  {
    path: ProtectedRoutes.ConfirmMissedEntryPage,
    component: LazyRoute,
    exact: true,
    children: <ConfirmMissedEntryPage />,
  },
  {
    path: ProtectedRoutes.PromotionsPage,
    component: LazyRoute,
    exact: true,
    children: <PromotionsPage />,
  },
  {
    path: ProtectedRoutes.RewardsPage,
    component: LazyRoute,
    exact: true,
    children: <RewardsPage />,
  },
  {
    path: ProtectedRoutes.ConfirmSubscription,
    component: LazyRoute,
    exact: true,
    children: <ConfirmSubscription />,
  },
  {
    path: ProtectedRoutes.SubscriptionConfirmed,
    component: LazyRoute,
    exact: true,
    children: <SubscriptionConfirmed />,
  },
  {
    path: ProtectedRoutes.LinkAccountPage,
    component: LazyRoute,
    exact: true,
    children: <LinkAccountPage />,
  },
  {
    path: ProtectedRoutes.ReceiptPage,
    component: LazyRoute,
    exact: true,
    children: <PassReceiptPage />,
  },
  {
    exact: true,
    path: ProtectedRoutes.ReserveParkingConfirmationPage,
    component: LazyRoute,
    children: <ReserveParkingConfirmationPage />,
  },
];
